import { EAttachmentStatus } from '@xometry/ui';
import { FormInstance } from 'antd';
import {
  StateAttachment,
  TemplateModalFormFieldEnum,
  TemplateModalFormValues,
} from 'components/TemplatesModal/TemplateModal.types';
import { Dispatch, SetStateAction } from 'react';
import { TemplateFragment } from 'utils/graphql/fragments/__generated__/template';
import { useRenderedTemplateQuery } from 'utils/graphql/queries/__generated__/renderedTemplate';

interface Args {
  form: FormInstance<TemplateModalFormValues>;
  selectedTemplate: TemplateFragment | null;
  sourceId?: string | null;
  sourceType?: string | null;
  setAttachments: Dispatch<SetStateAction<StateAttachment[]>>;
}

export const useRenderedTemplateSync = ({ form, selectedTemplate, sourceId, sourceType, setAttachments }: Args) => {
  const { data, loading } = useRenderedTemplateQuery({
    fetchPolicy: 'network-only',
    variables: {
      id: selectedTemplate?.id as string,
      sourceId: sourceId as string,
      sourceType: sourceType as string,
    },
    skip: !selectedTemplate || !sourceId,
    onCompleted: (response) => {
      const renderedTemplate = response.messengerRenderTemplate;

      if (!renderedTemplate) {
        return;
      }

      const {
        content,
        recipients = [],
        carbonCopy = [],
        blindCarbonCopy = [],
        attachments = [],
        documents = [],
        prepareAttachments = [],
      } = renderedTemplate;

      form.setFieldValue(TemplateModalFormFieldEnum.To, recipients);
      form.setFieldValue(TemplateModalFormFieldEnum.Cc, carbonCopy);
      form.setFieldValue(TemplateModalFormFieldEnum.Bcc, blindCarbonCopy);
      form.setFieldValue(TemplateModalFormFieldEnum.Content, content);

      const preparedAttachments = prepareAttachments.map((prepareAttachment) => ({
        id: prepareAttachment.uuid,
        uuidId: prepareAttachment.uuid,
        status: EAttachmentStatus.PREVIEW_SUCCESS,
        ...prepareAttachment,
      }));

      const initialAttachments = [
        ...(attachments as StateAttachment[]),
        ...(documents as StateAttachment[]),
        ...preparedAttachments,
      ];

      setAttachments((prev) => [...initialAttachments, ...prev]);
    },
  });

  return {
    renderedTemplateData: data,
    renderedTemplateLoading: loading,
  };
};
